import {
  LitElement,
  html,
  nothing,
  type CSSResultGroup,
  type PropertyValues,
  type TemplateResult,
} from 'lit';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './styles.js';

import { SortController, type SortMeta, type SortMode, type SortOrder } from './sort.controller.js';
import {
  FilterController,
  FilterService,
  type DataTableFilterMeta,
  type FilterMatchMode,
  type FilterElementOptions,
  type FilterMenuItem,
} from './filter.controller.js';
import { RowExpansionController } from './row-expansion.controller.js';
import { SelectionController } from './selection.controller.js';
import { EditController, type DataTableEditMode } from './edit.controller.js';
import {
  renderBuiltinFilter,
  renderFilterButton,
  renderFilterRow,
  renderGlobalFilter,
} from './filter.renderers.js';
import {
  type DataTableSize,
  type DataTableLoadingMode,
  type DataTablePaginatorPos,
  type DataTableSelectionMode,
  type DataTableColumnDef,
} from './types.js';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export public types consumers may need
// ─────────────────────────────────────────────────────────────────────────────

export type {
  DataTableSize,
  DataTableLoadingMode,
  DataTablePaginatorPos,
  DataTableSelectionMode,
  DataTableEditMode,
  DataTableColumnDef,
  DataTableFilterMeta,
  FilterMatchMode,
  FilterElementOptions,
  FilterMenuItem,
  SortMeta,
  SortMode,
  SortOrder,
};

// silence unused import warning — renderBuiltinFilter is used inside filter.renderers
// but re-exported here so consumers can use it in custom filterElement callbacks
export { renderBuiltinFilter };

// ─────────────────────────────────────────────────────────────────────────────
// Internal event — columns notify the parent table on any change
// ─────────────────────────────────────────────────────────────────────────────

const COLUMN_UPDATE = 'uwc-column-update';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-column
//
// Declarative column descriptor element. Has no visual output; it is a pure
// configuration node that the parent `uwc-datatable` reads via its light DOM.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-column
 *
 * Declarative column descriptor for `uwc-datatable`. Place inside the
 * datatable as a child — it carries no visual output of its own.
 *
 * @attr field            - Data key (supports dot-notation).
 * @attr header           - Column header text.
 * @attr footer           - Column footer text.
 * @attr col-style        - Inline style for the header <th>.
 * @attr col-class        - CSS class for the header <th>.
 * @attr hidden           - Hide this column.
 * @attr sortable         - Column is sortable.
 * @attr filter           - Show built-in filter input for this column.
 *
 * @prop bodyTemplate     - (JS) Function(rowData, rowIndex) → TemplateResult | string.
 * @prop headerTemplate   - (JS) Function(col) → TemplateResult | string.
 * @prop footerTemplate   - (JS) Function(col) → TemplateResult | string.
 * @prop cellClass        - (JS) Function(rowData, rowIndex) → string.
 * @prop cellStyle        - (JS) Function(rowData, rowIndex) → string | Record<string,string>.
 * @prop filterElement    - (JS) Function(options: FilterElementOptions) → TemplateResult.
 * @attr editable         - Mark this column's cells as editable (used with edit-mode on the table).
 * @prop editTemplate     - (JS) Function(rowData, rowIndex) → TemplateResult rendered in edit mode.
 */
export class UwcColumn extends LitElement {
  override createRenderRoot(): this { return this; }

  @property()                              field?: string;
  @property()                              header?: string;
  @property()                              footer?: string;
  @property({ attribute: 'col-style' })    colStyle?: string;
  @property({ attribute: 'col-class' })    colClass?: string;
  @property({ type: Boolean })             hidden   = false;
  @property({ type: Boolean })             sortable = false;
  @property({ type: Boolean })             filter   = false;

  @property({ attribute: false })
  filterElement?: (options: FilterElementOptions) => TemplateResult;

  @property({ attribute: 'filter-placeholder' }) filterPlaceholder = 'Search...';
  @property({ attribute: 'filter-menu-type' })   filterMenuType?: 'multiselect' | 'dropdown';
  @property({ attribute: false })                filterMenuItems?: FilterMenuItem[];
  @property({ attribute: 'filter-match-mode' })  filterMatchMode: FilterMatchMode = 'contains';
  @property()                                     type?: 'radio' | 'checkbox' | 'expander';

  @property({ attribute: false })
  bodyTemplate?: (rowData: Record<string, unknown>, rowIndex: number) => TemplateResult | string;

  @property({ attribute: false })
  headerTemplate?: (col: DataTableColumnDef) => TemplateResult | string;

  @property({ attribute: false })
  footerTemplate?: (col: DataTableColumnDef) => TemplateResult | string;

  @property({ attribute: false })
  cellClass?: (rowData: Record<string, unknown>, rowIndex: number) => string;

  @property({ attribute: false })
  cellStyle?: (rowData: Record<string, unknown>, rowIndex: number) => string | Record<string, string>;

  @property({ type: Boolean })
  editable = false;

  @property({ attribute: false })
  editTemplate?: (rowData: Record<string, unknown>, rowIndex: number) => TemplateResult | string;

  override connectedCallback() {
    super.connectedCallback();
    this._notify();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._notify();
  }

  protected override updated(): void {
    this._notify();
  }

  private _notify() {
    this.dispatchEvent(
      new CustomEvent(COLUMN_UPDATE, { bubbles: true, composed: true }),
    );
  }

  override render() { return nothing; }
}

// ══════════════════════════════════════════════════════════════════════════════
// uwc-datatable
//
// Accessible, framework-agnostic data table.
// All feature logic lives in dedicated controllers; this class is the
// orchestrator: it wires props → controllers, owns the render tree,
// and dispatches top-level events.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-datatable
 *
 * @attr value                         - (property) Array of row data objects.
 * @attr columns                       - (property) Array of DataTableColumnDef.
 * @attr data-key                      - Field used as unique row identifier.
 * @attr size                          - small | normal | large. Default: normal.
 * @attr show-gridlines                - Show borders between all cells.
 * @attr striped-rows                  - Alternate row background colour.
 * @attr table-style                   - Inline style applied to the inner <table>.
 * @attr table-class                   - CSS class applied to the inner <table>.
 * @attr caption                       - Table caption text.
 * @attr empty-message                 - Text shown when value is empty.
 * @attr loading                       - Show busy state.
 * @attr loading-mode                  - mask | skeleton. Default: mask.
 * @attr loading-icon                  - uwc-icon name for mask spinner.
 * @attr skeleton-rows                 - Number of skeleton rows when loading-mode=skeleton.
 * @attr paginator                     - Enable pagination.
 * @attr rows                          - Rows per page. Default: 10.
 * @attr first                         - Index of first record (reflects).
 * @attr total-records                 - Total record count (server-side mode).
 * @attr rows-per-page-options         - Comma-separated rows-per-page options.
 * @attr show-current-page-report      - Show "X of Y" report in paginator.
 * @attr current-page-report-template  - Template tokens: {currentPage} {totalPages} etc.
 * @attr paginator-position            - top | bottom | both. Default: bottom.
 * @attr show-jump-to-page-input       - Show jump-to-page input in paginator.
 * @attr show-jump-to-page-dropdown    - Show jump-to-page dropdown in paginator.
 * @attr always-show-paginator         - Always render paginator even on single page.
 * @attr sort-field                    - Initial sort field key.
 * @attr sort-order                    - Initial sort order: 1 (asc) | -1 (desc).
 * @attr sort-mode                     - single | multiple. Default: single.
 * @attr removable-sort                - Three-state cycling: asc → desc → none.
 * @attr filter-display                - row | menu. Default: row.
 * @attr global-filter-placeholder     - Placeholder for the global search input.
 *
 * @prop multiSortMeta        - Array of { field, order } for multi-column presort.
 * @prop customComparator     - Function(v1, v2, field, order) → number.
 * @prop filters              - DataTableFilterMeta — initial/controlled per-column filters.
 * @prop globalFilterFields   - string[] — fields searched by the global filter.
 * @prop rowClass             - Function(rowData, rowIndex) → string | Record<string,boolean>.
 * @prop rowStyle             - Function(rowData, rowIndex) → string | Record<string,string>.
 * @prop rowExpansionTemplate - Function(rowData, rowIndex) → TemplateResult.
 * @prop expandedRows         - Set of key values for currently expanded rows.
 * @prop rowExpandDisabled    - Function(rowData) → boolean.
 * @prop selection            - Currently selected row(s).
 * @prop selectionMode        - single | multiple.
 * @prop metaKeySelection     - Whether meta key is required to modify selection.
 * @attr edit-mode            - cell | row. Enables inline editing.
 * @attr row-edit-icon        - uwc-icon name for the init-edit button. Default: pencil.
 * @attr row-save-icon        - uwc-icon name for the save button. Default: check-lg.
 * @attr row-cancel-icon      - uwc-icon name for the cancel button. Default: x-lg.
 * @prop editingRowKeys       - Map whose key is the dataKey value; any value > 0 means the row starts in edit mode.
 * @prop rowEditDisabled      - Function(rowData) → boolean. Return true to prevent editing that row.
 *
 * @fires uwc-cell-edit-init     - { data, field, index } — cell entered edit mode.
 * @fires uwc-cell-edit-complete - { data, field, index } — cell edit confirmed.
 * @fires uwc-cell-edit-cancel   - { data, field, index } — cell edit cancelled; original value restored.
 * @fires uwc-row-edit-init      - { data, index } — row entered edit mode.
 * @fires uwc-row-edit-save      - { data, index } — row edit saved.
 * @fires uwc-row-edit-cancel    - { data, originalData, index } — row edit cancelled; original values restored.
 *
 * @slot header  - Content rendered above the table wrapper.
 * @slot footer  - Content rendered below the paginator.
 * @slot empty   - Custom empty-state content.
 * @slot (default) - Place uwc-column elements here.
 *
 * @fires uwc-row-click        - { row, index, originalEvent }
 * @fires uwc-row-hover        - { row, index, originalEvent }
 * @fires uwc-page-change      - { first, rows, page, pageCount }
 * @fires uwc-sort             - { sortField, sortOrder, sortMode, multiSortMeta }
 * @fires uwc-filter           - { filters, globalFilterValue }
 * @fires uwc-row-expand       - { data, index }
 * @fires uwc-row-collapse     - { data, index }
 * @fires uwc-row-select       - { data, index, originalEvent }
 * @fires uwc-row-unselect     - { data, index, originalEvent }
 * @fires uwc-selection-change - { selection }
 */
export class UwcDatatable extends LitElement {
  static override styles: CSSResultGroup = styles;

  // ── Data ───────────────────────────────────────────────────────────────────

  @property({ type: Array, attribute: false })
  value: Record<string, unknown>[] = [];

  @property({ type: Array, attribute: false })
  columns?: DataTableColumnDef[];

  @property({ attribute: 'data-key' })
  dataKey?: string;

  // ── Display ────────────────────────────────────────────────────────────────

  @property({ reflect: true })
  size: DataTableSize = 'normal';

  @property({ type: Boolean, reflect: true, attribute: 'show-gridlines' })
  showGridlines = false;

  @property({ type: Boolean, reflect: true, attribute: 'striped-rows' })
  stripedRows = false;

  // ── Table element ──────────────────────────────────────────────────────────

  @property({ attribute: 'table-style' })
  tableStyle?: string;

  @property({ attribute: 'table-class' })
  tableClass?: string;

  @property()
  caption?: string;

  @property({ attribute: 'empty-message' })
  emptyMessage = 'No records found.';

  // ── Loading ────────────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true })
  loading = false;

  @property({ attribute: 'loading-mode' })
  loadingMode: DataTableLoadingMode = 'mask';

  @property({ attribute: 'loading-icon' })
  loadingIcon = 'arrow-clockwise';

  @property({ type: Number, attribute: 'skeleton-rows' })
  skeletonRows = 5;

  // ── Conditional styling ────────────────────────────────────────────────────

  @property({ attribute: false })
  rowClass?: (rowData: Record<string, unknown>, rowIndex: number) => string | Record<string, boolean>;

  @property({ attribute: false })
  rowStyle?: (rowData: Record<string, unknown>, rowIndex: number) => string | Record<string, string>;

  // ── Pagination ─────────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true })
  paginator = false;

  @property({ type: Number, reflect: true })
  rows = 10;

  @property({ type: Number, reflect: true })
  first = 0;

  @property({ type: Number, attribute: 'total-records' })
  totalRecords?: number;

  @property({ attribute: 'rows-per-page-options' })
  rowsPerPageOptions?: string;

  @property({ type: Boolean, attribute: 'show-current-page-report' })
  showCurrentPageReport = false;

  @property({ attribute: 'current-page-report-template' })
  currentPageReportTemplate = '({currentPage} of {totalPages})';

  @property({ attribute: 'paginator-position', reflect: true })
  paginatorPosition: DataTablePaginatorPos = 'bottom';

  @property({ type: Boolean, attribute: 'show-jump-to-page-input' })
  showJumpToPageInput = false;

  @property({ type: Boolean, attribute: 'show-jump-to-page-dropdown' })
  showJumpToPageDropdown = false;

  @property({ type: Boolean, attribute: 'always-show-paginator' })
  alwaysShowPaginator = true;

  // ── Sort ───────────────────────────────────────────────────────────────────

  @property({ attribute: 'sort-field' })
  sortField: string | null = null;

  @property({ type: Number, attribute: 'sort-order' })
  sortOrder: SortOrder = 1;

  @property({ attribute: 'sort-mode' })
  sortMode: SortMode = 'single';

  @property({ type: Array, attribute: false })
  multiSortMeta: SortMeta[] = [];

  @property({ type: Boolean, attribute: 'removable-sort' })
  removableSort = false;

  @property({ attribute: false })
  customComparator?: (v1: unknown, v2: unknown, field: string, order: SortOrder) => number;

  // ── Filter ─────────────────────────────────────────────────────────────────

  @property({ type: Object, attribute: false })
  filters?: DataTableFilterMeta;

  @property({ type: Array, attribute: false })
  globalFilterFields?: string[];

  @property({ attribute: 'filter-display', reflect: true })
  filterDisplay: 'row' | 'menu' = 'row';

  @property({ attribute: 'global-filter-placeholder' })
  globalFilterPlaceholder = 'Search...';

  @property({ attribute: 'global-filter-value' })
  globalFilterValue?: string;

  // ── Row Expansion ──────────────────────────────────────────────────────────

  @property({ attribute: false })
  rowExpansionTemplate?: (rowData: Record<string, unknown>, rowIndex: number) => TemplateResult;

  @property({ attribute: false })
  expandedRows?: Set<unknown>;

  @property({ attribute: false })
  rowExpandDisabled?: (rowData: Record<string, unknown>) => boolean;

  @property({ attribute: 'row-expand-icon' })
  rowExpandIcon = 'chevron-right';

  @property({ attribute: 'row-collapse-icon' })
  rowCollapseIcon = 'chevron-down';

  // ── Selection ──────────────────────────────────────────────────────────────

  @property({ attribute: 'selection-mode' })
  selectionMode?: DataTableSelectionMode;

  @property({ attribute: false })
  selection?: Record<string, unknown> | Record<string, unknown>[] | null;

  @property({ type: Boolean, attribute: 'meta-key-selection' })
  metaKeySelection?: boolean;

  // ── Edit ───────────────────────────────────────────────────────────────────

  /** Enable inline editing. 'cell' — one cell at a time. 'row' — entire row at once. */
  @property({ attribute: 'edit-mode' })
  editMode?: DataTableEditMode;

  /**
   * Pre-seed rows in edit mode. Keys are `dataKey` values; any value > 0 means editing.
   * Only meaningful when `editMode="row"`.
   */
  @property({ type: Object, attribute: false })
  editingRowKeys?: Map<unknown, number>;

  /** Disable row editing for a specific row. Only meaningful when `editMode="row"`. */
  @property({ attribute: false })
  rowEditDisabled?: (rowData: Record<string, unknown>) => boolean;

  @property({ attribute: 'row-edit-icon' })
  rowEditIcon = 'pencil';

  @property({ attribute: 'row-save-icon' })
  rowSaveIcon = 'check-lg';

  @property({ attribute: 'row-cancel-icon' })
  rowCancelIcon = 'x-lg';

  // ── Internal: resolved uwc-column children ─────────────────────────────────

  @state() private _columnEls: UwcColumn[] = [];

  private _mo!: MutationObserver;

  // ── Controllers ────────────────────────────────────────────────────────────

  private _sort      = new SortController(this);
  private _filter    = new FilterController(this);
  private _expansion = new RowExpansionController(this, (row) => this._getRowKey(row));
  private _selection = new SelectionController(this, (row) => this._getRowKey(row));
  private _edit      = new EditController(this, (row) => this._getRowKey(row));

  private readonly _filterDebounceMap = new Map<string, ReturnType<typeof setTimeout>>();

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(COLUMN_UPDATE as keyof HTMLElementEventMap, this._onColumnUpdate);
    this._mo = new MutationObserver(() => this._refreshColumnEls());
    this._mo.observe(this, { childList: true });
    this._refreshColumnEls();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(COLUMN_UPDATE as keyof HTMLElementEventMap, this._onColumnUpdate);
    this._mo?.disconnect();
  }

  override willUpdate(changed: PropertyValues) {
    // ── Sort ─────────────────────────────────────────────────────────────────
    if (changed.has('sortField'))        this._sort.sortField        = this.sortField;
    if (changed.has('sortOrder'))        this._sort.sortOrder        = this.sortOrder;
    if (changed.has('sortMode'))         this._sort.sortMode         = this.sortMode;
    if (changed.has('multiSortMeta'))    this._sort.multiSortMeta    = this.multiSortMeta ?? [];
    if (changed.has('removableSort'))    this._sort.removableSort    = this.removableSort;
    if (changed.has('customComparator')) this._sort.customComparator = this.customComparator;

    // ── Filter ────────────────────────────────────────────────────────────────
    if (changed.has('filters') && this.filters) {
      this._filter.filters = { ...this.filters };
    }
    if (changed.has('globalFilterFields')) {
      this._filter.globalFilterFields = this.globalFilterFields ?? [];
    }
    if (changed.has('globalFilterValue') && this.globalFilterValue !== undefined) {
      this._filter.globalFilterValue = this.globalFilterValue;
    }

    // ── Row Expansion ─────────────────────────────────────────────────────────
    if (changed.has('expandedRows')) {
      this._expansion.syncFromProp(this.expandedRows);
    }

    // ── Selection ─────────────────────────────────────────────────────────────
    if (changed.has('selectionMode'))    this._selection.selectionMode    = this.selectionMode;
    if (changed.has('metaKeySelection')) this._selection.metaKeySelection = this.metaKeySelection;
    if (changed.has('selection') || changed.has('dataKey')) {
      this._selection.syncFromProp(this.selection);
    }

    // ── Edit ──────────────────────────────────────────────────────────────────
    if (changed.has('editMode'))       this._edit.editMode = this.editMode;
    if (changed.has('editingRowKeys')) this._edit.syncRowKeys(this.editingRowKeys);
  }

  // ── Internal helpers ───────────────────────────────────────────────────────

  private _onColumnUpdate = () => { this._refreshColumnEls(); };

  private _refreshColumnEls() {
    this._columnEls = Array.from(
      this.querySelectorAll<UwcColumn>(':scope > uwc-column'),
    );
    this.requestUpdate();
  }

  /** Resolve row key using dot-notation dataKey, falling back to row identity. */
  private _getRowKey(row: Record<string, unknown>): unknown {
    return this.dataKey ? FilterService.resolve(row, this.dataKey) : row;
  }

  /** Resolve effective column definitions. `columns` prop takes priority. */
  private _effectiveCols(): DataTableColumnDef[] {
    if (this.columns?.length) {
      return this.columns.filter(c => !c.hidden);
    }
    return this._columnEls
      .filter(el => !el.hidden)
      .map(el => ({
        field:             el.field,
        header:            el.header,
        footer:            el.footer,
        style:             el.colStyle,
        class:             el.colClass,
        hidden:            el.hidden,
        sortable:          el.sortable,
        filter:            el.filter,
        filterElement:     el.filterElement,
        filterPlaceholder: el.filterPlaceholder,
        filterMenuType:    el.filterMenuType,
        filterMenuItems:   el.filterMenuItems,
        filterMatchMode:   el.filterMatchMode,
        type:              el.type,
        bodyTemplate:      el.bodyTemplate,
        headerTemplate:    el.headerTemplate,
        footerTemplate:    el.footerTemplate,
        cellClass:         el.cellClass,
        cellStyle:         el.cellStyle,
        editable:          el.editable,
        editTemplate:      el.editTemplate,
      }));
  }

  private _colHasFilter(col: DataTableColumnDef): boolean {
    return !!(col.filter || col.filterMenuType || col.filterElement);
  }

  private _hasFilterRow(cols: DataTableColumnDef[]): boolean {
    return this.filterDisplay === 'row' && cols.some(c => this._colHasFilter(c));
  }

  private _hasAnyFooter(cols: DataTableColumnDef[]): boolean {
    return cols.some(c => c.footer || c.footerTemplate);
  }

  private _visibleRows(allRows: Record<string, unknown>[]): Record<string, unknown>[] {
    if (!this.paginator) return allRows;
    return allRows.slice(this.first, this.first + this.rows);
  }

  private _totalRecords(allRows: Record<string, unknown>[]): number {
    return this.totalRecords ?? allRows.length;
  }

  // ── Selection dispatch ─────────────────────────────────────────────────────

  private _emitSelectionChange(): void {
    this.dispatchEvent(new CustomEvent('uwc-selection-change', {
      bubbles: true, composed: true,
      detail: { selection: this._selection.buildOutput(this.value ?? []) },
    }));
  }

  // ── Row handlers ───────────────────────────────────────────────────────────

  private _onRowClick(
    row: Record<string, unknown>,
    index: number,
    e: MouseEvent,
    hasSelectionCol = false,
  ) {
    this.dispatchEvent(new CustomEvent('uwc-row-click', {
      bubbles: true, composed: true,
      detail: { row, index, originalEvent: e },
    }));
    if (this.selectionMode && !hasSelectionCol) {
      const allRows = this._sort.sortedData(this._filter.filteredData(this.value ?? []));
      this._selection.handleClick(row, index, e, allRows);
      this._emitSelectionChange();
    }
  }

  private _onRowMouseEnter(row: Record<string, unknown>, index: number, e: MouseEvent) {
    this.dispatchEvent(new CustomEvent('uwc-row-hover', {
      bubbles: true, composed: true,
      detail: { row, index, originalEvent: e },
    }));
  }

  // ── Sort / filter / page handlers ──────────────────────────────────────────

  private _onSort(field: string) {
    if (this.paginator) this.first = 0;
    this._sort.toggleSort(field);
  }

  private _onFilterChange(field: string, value: unknown, matchMode?: FilterMatchMode): void {
    const existing = this._filterDebounceMap.get(field);
    if (existing !== undefined) clearTimeout(existing);
    const t = setTimeout(() => {
      if (this.paginator) this.first = 0;
      this._filter.updateFilter(field, value, matchMode);
      this._filterDebounceMap.delete(field);
    }, 250);
    this._filterDebounceMap.set(field, t);
  }

  private _onGlobalFilter(value: string): void {
    const existing = this._filterDebounceMap.get('__global__');
    if (existing !== undefined) clearTimeout(existing);
    const t = setTimeout(() => {
      if (this.paginator) this.first = 0;
      this._filter.updateGlobal(value);
      this._filterDebounceMap.delete('__global__');
    }, 250);
    this._filterDebounceMap.set('__global__', t);
  }

  private _onPageChange(e: Event) {
    const detail = (e as CustomEvent).detail as { first: number; rows: number };
    this.first = detail.first;
    this.rows  = detail.rows;
    this.dispatchEvent(new CustomEvent('uwc-page-change', {
      bubbles: true, composed: true, detail,
    }));
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Programmatically clear all active column filters and the global filter value. */
  clearFilters(): void {
    this._filter.clearAll();
  }

  /** Expose FilterService for consumers who need standalone matching. */
  static readonly FilterService = FilterService;

  // ── Cell renderers ─────────────────────────────────────────────────────────

  private _renderHeaderCell(col: DataTableColumnDef): TemplateResult {
    if (col.type === 'row-editor') {
      return html`
        <th
          part="header-cell"
          class="uwc-dt__th uwc-dt__th--row-editor"
          scope="col"
          role="columnheader"
          aria-label="Row actions"
        ></th>`;
    }

    if (col.type === 'expander') {
      return html`
        <th
          part="header-cell"
          class="uwc-dt__th uwc-dt__th--expander"
          scope="col"
          role="columnheader"
          aria-label="Row expansion"
        ></th>`;
    }

    if (col.type === 'radio') {
      return html`
        <th
          part="header-cell"
          class="uwc-dt__th uwc-dt__th--radio"
          scope="col"
          role="columnheader"
          aria-label="Select"
        ></th>`;
    }

    if (col.type === 'checkbox') {
      const allRows = this._sort.sortedData(this._filter.filteredData(this.value ?? []));
      const total   = allRows.length;
      const cnt     = total > 0 ? allRows.filter(r => this._selection.isSelected(r)).length : 0;
      const isAll   = total > 0 && cnt === total;
      const isSome  = cnt > 0 && cnt < total;
      return html`
        <th
          part="header-cell"
          class="uwc-dt__th uwc-dt__th--checkbox"
          scope="col"
          role="columnheader"
        >
          <uwc-checkbox
            .checked=${isAll}
            .indeterminate=${isSome}
            aria-label="Select all rows"
            @click=${(e: Event) => e.stopPropagation()}
            @uwc-change=${(e: CustomEvent) => {
              e.stopPropagation();
              this._selection.toggleAll(allRows, isAll);
              this._emitSelectionChange();
            }}
          ></uwc-checkbox>
        </th>`;
    }

    const label: TemplateResult | string = col.headerTemplate
      ? col.headerTemplate(col)
      : (col.header ?? col.field ?? '');

    const showFilterBtn = this.filterDisplay === 'menu' && this._colHasFilter(col);

    if (!col.sortable && !showFilterBtn) {
      return html`
        <th
          part="header-cell"
          class="uwc-dt__th ${col.class ?? ''}"
          style=${ifDefined(col.style)}
          scope="col"
          role="columnheader"
        >${label}</th>`;
    }

    const colState = col.sortable ? this._sort.getColumnState(col.field ?? '') : null;
    const ariaSort  = colState?.sorted
      ? (colState.order === 1 ? 'ascending' : 'descending')
      : (col.sortable ? 'none' : undefined);
    const iconName  = colState?.sorted
      ? (colState.order === 1 ? 'sort-down' : 'sort-up')
      : 'arrow-down-up';

    return html`
      <th
        part="header-cell"
        class="uwc-dt__th ${col.sortable ? 'uwc-dt__th--sortable' : ''} ${col.class ?? ''}"
        style=${ifDefined(col.style)}
        scope="col"
        role="columnheader"
        aria-sort=${ifDefined(ariaSort)}
        tabindex=${col.sortable ? '0' : nothing as unknown as string}
        @click=${col.sortable ? () => this._onSort(col.field ?? '') : nothing}
        @keydown=${col.sortable ? (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onSort(col.field ?? '');
          }
        } : nothing}
      >
        <span class="uwc-dt__th-content">
          <span class="uwc-dt__th-label">${label}</span>
          ${colState ? html`
            <uwc-icon
              name=${iconName}
              size="1em"
              class=${classMap({ 'uwc-dt__sort-icon': true, 'uwc-dt__sort-icon--active': colState.sorted })}
              aria-hidden="true"
            ></uwc-icon>
            ${colState.rank !== undefined
              ? html`<span class="uwc-dt__sort-rank" aria-hidden="true">${colState.rank}</span>`
              : nothing}
          ` : nothing}
          ${showFilterBtn ? this._renderFilterButtonForCol(col) : nothing}
        </span>
      </th>`;
  }

  /** Bridge: resolves filter state for a column then calls the pure renderer. */
  private _renderFilterButtonForCol(col: DataTableColumnDef): TemplateResult {
    const field     = col.field ?? '';
    const meta      = this._filter.filters[field];
    const filterVal = meta?.value ?? '';
    const matchMode = meta?.matchMode ?? col.filterMatchMode ?? 'contains';
    return renderFilterButton(
      col,
      this._filter.isActive(field),
      filterVal,
      matchMode,
      (v, mm) => this._onFilterChange(field, v, mm),
    );
  }

  /** Render the save/cancel (or init-edit) controls for `type='row-editor'`. */
  private _renderRowEditorCell(row: Record<string, unknown>, rowIdx: number): TemplateResult {
    const key      = this._getRowKey(row);
    const editing  = this._edit.isRowEditing(key);
    const disabled = this.rowEditDisabled ? this.rowEditDisabled(row) : false;

    return html`
      <td part="body-cell" class="uwc-dt__td uwc-dt__td--row-editor" role="cell">
        ${editing ? html`
          <span class="uwc-dt__row-editor-controls">
            <button
              type="button"
              class="uwc-dt__row-editor-btn uwc-dt__row-editor-btn--save"
              aria-label="Save row"
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                this._edit.saveRowEdit(row, rowIdx);
              }}
            >
              <uwc-icon name=${this.rowSaveIcon} size="1em" aria-hidden="true"></uwc-icon>
            </button>
            <button
              type="button"
              class="uwc-dt__row-editor-btn uwc-dt__row-editor-btn--cancel"
              aria-label="Cancel editing"
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                this._edit.cancelRowEdit(row, rowIdx);
              }}
            >
              <uwc-icon name=${this.rowCancelIcon} size="1em" aria-hidden="true"></uwc-icon>
            </button>
          </span>
        ` : html`
          <button
            type="button"
            class="uwc-dt__row-editor-btn uwc-dt__row-editor-btn--init"
            aria-label="Edit row"
            ?disabled=${disabled}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (!disabled) this._edit.initRowEdit(row, rowIdx);
            }}
          >
            <uwc-icon name=${this.rowEditIcon} size="1em" aria-hidden="true"></uwc-icon>
          </button>
        `}
      </td>`;
  }

  private _renderBodyCell(
    col: DataTableColumnDef,
    row: Record<string, unknown>,
    rowIdx: number,
  ): TemplateResult {
    if (col.type === 'row-editor') {
      return this._renderRowEditorCell(row, rowIdx);
    }

    if (col.type === 'expander') {
      const expanded = this._expansion.isExpanded(row);
      const disabled = this.rowExpandDisabled ? this.rowExpandDisabled(row) : false;
      return html`
        <td part="body-cell" class="uwc-dt__td uwc-dt__td--expander" role="cell">
          <button
            type="button"
            class="uwc-dt__expansion-toggle"
            aria-expanded=${String(expanded)}
            aria-label=${expanded ? 'Collapse row' : 'Expand row'}
            ?disabled=${disabled}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (!disabled) this._expansion.toggle(row, rowIdx);
            }}
          >
            <uwc-icon
              name=${expanded ? this.rowCollapseIcon : this.rowExpandIcon}
              size="1em"
              aria-hidden="true"
            ></uwc-icon>
          </button>
        </td>`;
    }

    if (col.type === 'radio') {
      const selected = this._selection.isSelected(row);
      return html`
        <td
          part="body-cell"
          class="uwc-dt__td uwc-dt__td--radio"
          role="cell"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            const allRows = this._sort.sortedData(this._filter.filteredData(this.value ?? []));
            this._selection.handleClick(row, rowIdx, e, allRows);
            this._emitSelectionChange();
          }}
        >
          <uwc-radiobutton .checked=${selected} aria-label="Select row"></uwc-radiobutton>
        </td>`;
    }

    if (col.type === 'checkbox') {
      const selected = this._selection.isSelected(row);
      return html`
        <td
          part="body-cell"
          class="uwc-dt__td uwc-dt__td--checkbox"
          role="cell"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            const allRows = this._sort.sortedData(this._filter.filteredData(this.value ?? []));
            this._selection.handleClick(row, rowIdx, e, allRows);
            this._emitSelectionChange();
          }}
        >
          <uwc-checkbox .checked=${selected} aria-label="Select row"></uwc-checkbox>
        </td>`;
    }

    const rowKey    = this._getRowKey(row);
    const field     = col.field ?? '';
    const dynClass  = col.cellClass ? col.cellClass(row, rowIdx) : '';

    let dynStyle: Record<string, string> = {};
    if (col.cellStyle) {
      const s = col.cellStyle(row, rowIdx);
      dynStyle = typeof s === 'string'
        ? Object.fromEntries(
            s.split(';')
              .filter(Boolean)
              .map(part => part.split(':').map(p => p.trim()) as [string, string]),
          )
        : s;
    }

    const styleAttr = Object.keys(dynStyle).length
      ? styleMap(dynStyle)
      : nothing as unknown as string;

    // ── Cell edit mode ────────────────────────────────────────────────────────
    if (this.editMode === 'cell' && col.editable && field) {
      const isEditing = this._edit.isCellEditing(rowKey, field);

      if (isEditing) {
        const editContent: TemplateResult | string = col.editTemplate
          ? col.editTemplate(row, rowIdx)
          : html`<input
              class="uwc-dt__cell-editor"
              type="text"
              .value=${String(row[field] ?? '')}
              autofocus
              aria-label=${col.header ?? field}
              @input=${(e: Event) => { row[field] = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') { e.preventDefault(); this._edit.completeCellEdit(row, field, rowIdx); }
                if (e.key === 'Escape') { e.preventDefault(); this._edit.cancelCellEdit(row, field, rowIdx); }
              }}
            />`;

        return html`
          <td
            part="body-cell"
            class="uwc-dt__td uwc-dt__td--editable uwc-dt__td--editing ${dynClass}"
            style=${styleAttr}
            role="cell"
            @focusout=${(e: FocusEvent) => {
              if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
                this._edit.completeCellEdit(row, field, rowIdx);
              }
            }}
          >${editContent}</td>`;
      }

      return html`
        <td
          part="body-cell"
          class="uwc-dt__td uwc-dt__td--editable ${dynClass}"
          style=${styleAttr}
          role="cell"
          tabindex="0"
          aria-label=${`${col.header ?? field}: ${String(row[field] ?? '')}, press Enter to edit`}
          @click=${(e: MouseEvent) => { e.stopPropagation(); this._edit.initCellEdit(row, field, rowIdx); }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); this._edit.initCellEdit(row, field, rowIdx); }
          }}
        >
          ${col.bodyTemplate ? col.bodyTemplate(row, rowIdx) : String(row[field] ?? '')}
          <uwc-icon class="uwc-dt__cell-edit-icon" name="pencil" size="0.8em" aria-hidden="true"></uwc-icon>
        </td>`;
    }

    // ── Row edit mode — editable cell ─────────────────────────────────────────
    if (this.editMode === 'row' && col.editable && field) {
      const isEditing = this._edit.isRowEditing(rowKey);

      if (isEditing) {
        const editContent: TemplateResult | string = col.editTemplate
          ? col.editTemplate(row, rowIdx)
          : html`<input
              class="uwc-dt__cell-editor"
              type="text"
              .value=${String(row[field] ?? '')}
              aria-label=${col.header ?? field}
              @input=${(e: Event) => { row[field] = (e.target as HTMLInputElement).value; }}
            />`;

        return html`
          <td
            part="body-cell"
            class="uwc-dt__td uwc-dt__td--editable uwc-dt__td--editing ${dynClass}"
            style=${styleAttr}
            role="cell"
          >${editContent}</td>`;
      }
    }

    // ── Default view ──────────────────────────────────────────────────────────
    let content: TemplateResult | string;
    if (col.bodyTemplate) {
      content = col.bodyTemplate(row, rowIdx);
    } else {
      const raw = field ? FilterService.resolve(row, field) : '';
      content = raw != null ? String(raw) : '';
    }

    return html`
      <td
        part="body-cell"
        class="uwc-dt__td ${dynClass}"
        style=${styleAttr}
        role="cell"
      >${content}</td>`;
  }

  private _renderFooterCell(col: DataTableColumnDef): TemplateResult {
    const content: TemplateResult | string | undefined = col.footerTemplate
      ? col.footerTemplate(col)
      : col.footer;
    return html`
      <td part="footer-cell" class="uwc-dt__td uwc-dt__td--footer" role="cell">
        ${content ?? nothing}
      </td>`;
  }

  private _renderSkeletonRows(cols: DataTableColumnDef[]): TemplateResult {
    return html`
      ${Array.from({ length: this.skeletonRows }, (_, i) => html`
        <tr part="body-row" class="uwc-dt__row--skeleton" role="row" aria-rowindex=${i + 1}>
          ${cols.map(() => html`
            <td part="body-cell" class="uwc-dt__td" role="cell">
              <div class="uwc-dt__skel"></div>
            </td>`)}
        </tr>`)}`;
  }

  private _renderPaginator(totalRecs: number, pos: 'top' | 'bottom'): TemplateResult {
    const cls = pos === 'top' ? 'uwc-dt__paginator uwc-dt__paginator--top' : 'uwc-dt__paginator';
    return html`
      <uwc-paginator
        class=${cls}
        .first=${this.first}
        .rows=${this.rows}
        total-records=${totalRecs}
        ?always-show=${this.alwaysShowPaginator}
        rows-per-page-options=${ifDefined(this.rowsPerPageOptions)}
        ?show-current-page-report=${this.showCurrentPageReport}
        current-page-report-template=${this.currentPageReportTemplate}
        ?show-jump-to-page-input=${this.showJumpToPageInput}
        ?show-jump-to-page-dropdown=${this.showJumpToPageDropdown}
        @uwc-page-change=${this._onPageChange}
      ></uwc-paginator>`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    const cols        = this._effectiveCols();
    const filtered    = this._filter.filteredData(this.value ?? []);
    const allRows     = this._sort.sortedData(filtered);
    const totalRecs   = this._totalRecords(allRows);
    const visibleRows = this._visibleRows(allRows);
    const hasFtr      = this._hasAnyFooter(cols);
    const showSkeleton    = this.loading && this.loadingMode === 'skeleton';
    const showMask        = this.loading && this.loadingMode === 'mask';
    const hasSelectionCol = cols.some(c => c.type === 'radio' || c.type === 'checkbox');

    const tableClasses = classMap({
      'uwc-dt__table': true,
      ...(this.tableClass ? { [this.tableClass]: true } : {}),
    });

    const showTop    = this.paginator && (this.paginatorPosition === 'top'    || this.paginatorPosition === 'both');
    const showBottom = this.paginator && (this.paginatorPosition === 'bottom' || this.paginatorPosition === 'both');

    return html`
      <slot name="header"></slot>

      ${this.filterDisplay === 'menu' &&
        this.globalFilterFields?.length &&
        this.globalFilterValue === undefined
        ? renderGlobalFilter(
            this._filter.globalFilterValue,
            this.globalFilterPlaceholder,
            (v) => this._onGlobalFilter(v),
          )
        : nothing}

      ${showTop ? this._renderPaginator(totalRecs, 'top') : nothing}

      <div
        class="uwc-dt__wrapper"
        part="wrapper"
        role="region"
        aria-label=${this.caption ?? 'Data table'}
        tabindex="0"
      >
        ${showMask ? html`
          <div class="uwc-dt__mask" part="mask" role="status" aria-label="Loading">
            <span class="uwc-dt__mask-icon" aria-hidden="true">
              <uwc-icon name=${this.loadingIcon} size="1.4em" spin></uwc-icon>
            </span>
          </div>` : nothing}

        <table
          class=${tableClasses}
          part="table"
          style=${ifDefined(this.tableStyle)}
          aria-busy=${this.loading ? 'true' : 'false'}
          aria-rowcount=${totalRecs}
          aria-colcount=${cols.length}
          role="table"
        >
          ${this.caption
            ? html`<caption class="uwc-dt__caption" part="caption">${this.caption}</caption>`
            : nothing}

          <thead class="uwc-dt__thead" part="thead" role="rowgroup">
            <tr part="thead-row" role="row">
              ${cols.map(col => this._renderHeaderCell(col))}
            </tr>
            ${this._hasFilterRow(cols)
              ? renderFilterRow(
                  cols,
                  this._filter.filters,
                  (col) => this._colHasFilter(col),
                  (field, v, mm) => this._onFilterChange(field, v, mm),
                )
              : nothing}
          </thead>

          <tbody class="uwc-dt__tbody" part="tbody" role="rowgroup">
            ${showSkeleton
              ? this._renderSkeletonRows(cols)
              : visibleRows.length === 0
                ? html`
                    <tr part="empty-row" role="row">
                      <td
                        part="empty-message"
                        class="uwc-dt__empty"
                        colspan=${cols.length}
                        role="cell"
                      >
                        <slot name="empty">${this.emptyMessage}</slot>
                      </td>
                    </tr>`
                : repeat(
                    visibleRows,
                    (row, i) => (this.dataKey ? row[this.dataKey] : this.first + i),
                    (row, i) => {
                      const absIdx = this.first + i;

                      let rowCls: Record<string, boolean> = {};
                      if (this.rowClass) {
                        const rc = this.rowClass(row, absIdx);
                        rowCls = typeof rc === 'string'
                          ? Object.fromEntries(rc.split(' ').filter(Boolean).map(c => [c, true]))
                          : rc;
                      }

                      let rowSty: Record<string, string> = {};
                      if (this.rowStyle) {
                        const rs = this.rowStyle(row, absIdx);
                        rowSty = typeof rs === 'string'
                          ? Object.fromEntries(
                              rs.split(';').filter(Boolean)
                                .map(p => p.split(':').map(s => s.trim()) as [string, string]),
                            )
                          : rs;
                      }

                      const selected           = this.selectionMode ? this._selection.isSelected(row) : false;
                      const rowSelectable      = this.selectionMode && !hasSelectionCol;
                      const shouldRenderExpand = !!this.rowExpansionTemplate && this._expansion.shouldRender(row);
                      const isCollapsing       = this._expansion.isCollapsing(row);

                      return html`
                        <tr
                          part="body-row"
                          class=${classMap({ ...rowCls, 'uwc-dt__row--selected': selected, 'uwc-dt__row--sel-col': hasSelectionCol })}
                          style=${Object.keys(rowSty).length ? styleMap(rowSty) : nothing as unknown as string}
                          role="row"
                          aria-rowindex=${absIdx + 1}
                          aria-selected=${this.selectionMode ? String(selected) : nothing as unknown as string}
                          tabindex=${rowSelectable ? '0' : nothing as unknown as string}
                          @click=${(e: MouseEvent) => this._onRowClick(row, absIdx, e, hasSelectionCol)}
                          @mouseenter=${(e: MouseEvent) => this._onRowMouseEnter(row, absIdx, e)}
                          @keydown=${rowSelectable ? (e: KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const sortedRows = this._sort.sortedData(this._filter.filteredData(this.value ?? []));
                              this._selection.handleClick(row, absIdx, e, sortedRows);
                              this._emitSelectionChange();
                            }
                          } : nothing}
                        >
                          ${cols.map(col => this._renderBodyCell(col, row, absIdx))}
                        </tr>
                        ${shouldRenderExpand ? html`
                          <tr
                            part="row-expansion"
                            class="uwc-dt__row--expansion"
                            role="row"
                            aria-hidden=${isCollapsing ? 'true' : 'false'}
                          >
                            <td colspan=${cols.length} role="cell" class="uwc-dt__td--expansion">
                              <div
                                class=${classMap({
                                  'uwc-dt__expansion-body': true,
                                  'uwc-dt__expansion-body--closing': isCollapsing,
                                })}
                                @animationend=${() => this._expansion.onAnimationEnd(row)}
                              >${this.rowExpansionTemplate!(row, absIdx)}</div>
                            </td>
                          </tr>` : nothing}`;
                    },
                  )}
          </tbody>

          ${hasFtr
            ? html`
                <tfoot class="uwc-dt__tfoot" part="tfoot" role="rowgroup">
                  <tr part="footer-row" role="row">
                    ${cols.map(col => this._renderFooterCell(col))}
                  </tr>
                </tfoot>`
            : nothing}
        </table>
      </div>

      ${showBottom ? this._renderPaginator(totalRecs, 'bottom') : nothing}

      <slot name="footer"></slot>

      <slot class="uwc-dt__col-slot" @slotchange=${this._refreshColumnEls}></slot>
    `;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Global element registry declarations
// ─────────────────────────────────────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'uwc-datatable': UwcDatatable;
    'uwc-column':    UwcColumn;
  }
}
